// Relative-deadline copy for upcoming public hearings.
//
// The upstream payload is the source of truth for the absolute
// `submission_deadline` timestamp (it already applies the City's
// "8 days before the meeting at noon Calgary time" rule per FR-012).
// This module's only job is to render the *relative* string a reader
// sees on the page — "submissions close in 3 days", "closing today",
// "closing tomorrow", "submissions closed" — in `America/Edmonton`
// regardless of the reader's device locale.
//
// Day-bucket math runs in the Calgary calendar so that "today" /
// "tomorrow" reflect the local day boundary, not the reader's. We use
// `Intl.DateTimeFormat` with `timeZone: "America/Edmonton"` rather
// than depending on `date-fns-tz`.

const TZ = "America/Edmonton";

export type RelativeDeadline = {
  label: string;
  closed: boolean;
};

export function relativeDeadline(
  submissionDeadlineUtc: string | null,
  now: Date = new Date(),
): RelativeDeadline {
  if (submissionDeadlineUtc == null) {
    return { label: "submission deadline not yet set", closed: false };
  }

  const deadline = new Date(submissionDeadlineUtc);
  if (Number.isNaN(deadline.getTime())) {
    return { label: "submission deadline unavailable", closed: false };
  }

  if (deadline.getTime() <= now.getTime()) {
    return { label: "submissions closed", closed: true };
  }

  const days = calgaryDayDelta(now, deadline);

  if (days === 0) {
    return { label: "closing today", closed: false };
  }
  if (days === 1) {
    return { label: "closing tomorrow", closed: false };
  }
  return { label: `submissions close in ${days} days`, closed: false };
}

// Number of Calgary-local calendar days between `from` and `to`, where
// the difference counts the *day boundaries* crossed, not the elapsed
// hours. So an event at 11pm Calgary time tonight returns 0 days from
// 8am this morning, and an event at 1am Calgary time tomorrow returns
// 1 day from 11pm tonight.
function calgaryDayDelta(from: Date, to: Date): number {
  const fromYmd = calgaryYmd(from);
  const toYmd = calgaryYmd(to);
  // Convert each y-m-d triple to a Date at UTC midnight so date-only
  // subtraction is unaffected by DST transitions in Calgary.
  const fromUtc = Date.UTC(fromYmd.y, fromYmd.m - 1, fromYmd.d);
  const toUtc = Date.UTC(toYmd.y, toYmd.m - 1, toYmd.d);
  const ms = toUtc - fromUtc;
  return Math.round(ms / 86_400_000);
}

// Calgary-local YYYY-MM-DD for any UTC instant. Lexicographic
// comparison of two such strings is equivalent to comparing the
// underlying Calgary calendar days, so it's the cheapest correct way
// to do day-level filtering without dealing with DST offsets in
// epoch-ms math.
export function calgaryYmdString(d: Date): string {
  const { y, m, d: day } = calgaryYmd(d);
  const mm = String(m).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function calgaryYmd(d: Date): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return {
    y: Number(map.year),
    m: Number(map.month),
    d: Number(map.day),
  };
}

// Render the upstream's data-refresh timestamp as Calgary local time
// for the "Data refreshed at <ts>" disclaimer (FR-016, FR-018a).
export function formatRefreshedAt(isoUtc: string): string {
  const d = new Date(isoUtc);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

// Bucket the snapshot age for the disclaimer's escalating visual
// emphasis (FR-018a). "fresh" under 1h, "stale" 1–6h,
// "very-stale" >6h or service-degraded.
export type FreshnessBucket = "fresh" | "stale" | "very-stale";

export function freshnessBucket(
  refreshedAtUtc: string,
  now: Date = new Date(),
  serviceDegraded = false,
): FreshnessBucket {
  if (serviceDegraded) return "very-stale";
  const refreshed = new Date(refreshedAtUtc).getTime();
  const ageMs = now.getTime() - refreshed;
  if (ageMs < 60 * 60 * 1000) return "fresh";
  if (ageMs < 6 * 60 * 60 * 1000) return "stale";
  return "very-stale";
}
